import { CardBody } from 'components';
import { Accordion } from 'components/atoms/Accordion/Accordion';
import { CustomPropertyDate } from 'components/atoms/CustomPropertyDate/CustomPropertyDate';
import { CustomPropertyInput } from 'components/atoms/CustomPropertyInput/CustomPropertyInput';
import { CustomPropertyLabel } from 'components/atoms/CustomPropertyLabel/CustomPropertyLabel';
import { CustomPropertyColorSelect } from 'components/atoms/CustomPropertyColorSelect/CustomPropertyColorSelect';
import { CustomPropertySelect } from 'components/atoms/CustomPropertySelect/CustomPropertySelect';
import { useDateValidation } from 'hooks/useDateValidation';
import { useVehiclesColors } from 'hooks/useVehiclesColors';
import { usePropertyDetails } from '../hooks/PropertyDetails.hook';
import { ApiProperty, Price, VehicleData } from '../types/Add-subscription.types';
import {
  isValidVin,
  sanitizeVinInput,
  VIN_ERROR_MESSAGE,
} from 'utils/vin.utils';
import { PropertyList } from './PropertyList';
import './PropertyDetails.css';

export interface PropertyListPropsForSlot {
  list: ApiProperty[];
  yearRange: { minYear: number; maxYear: number } | null;
  stripePriceId: string;
}

interface PropertyDetailsProps {
  counts: Record<number, number>;
  prices: Price[];
  period: string;
  onValidChange: (isValid: boolean) => void;
  formData: Record<string, Record<string, string>>;
  setFormData: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, string>>>
  >;
  vehicleData: VehicleData;
  branch?: string;
  /** Slots con propiedad seleccionada no muestran formulario; undefined = sin elegir */
  selectedPropertyByKey?: Record<string, ApiProperty | null>;
  onSelectPropertyForSlot?: (
    slotKey: string,
    property: ApiProperty | null | undefined
  ) => void;
  propertyListPropsByKey?: Record<string, PropertyListPropsForSlot>;
  loadingProperties?: boolean;
  errorProperties?: boolean;
}

const countryOptions = [
  'Puerto Rico',
  'Costa Rica',
  'Panama',
  'Colombia',
  'México',
];

export function PropertyDetails({
  counts,
  prices,
  period,
  onValidChange,
  formData,
  setFormData,
  vehicleData,
  branch = 'pr',
  selectedPropertyByKey = {},
  onSelectPropertyForSlot,
  propertyListPropsByKey = {},
  loadingProperties = false,
  errorProperties = false,
}: PropertyDetailsProps) {
  const { minDate, maxDate } = useDateValidation();
  const { colors: vehiclesColors } = useVehiclesColors();

  const {
    propertyEntries2,
    handleInputChange,
    selectedBrands,
    setSelectedBrands,
    getIcon,
  } = usePropertyDetails({
    prices,
    period,
    counts,
    formData,
    setFormData,
    onValidChange,
    selectedPropertyByKey,
  });

  if (propertyEntries2.length === 0) return null;

  const isVehicleType = (type: string) => {
    const t = type.toLowerCase();
    return t === 'vehiculos' || t === 'motocicleta';
  };

  return (
    <CardBody>
      <header className="addon-group__header">
        <h2 className="addon-group__title">PROPIEDADES</h2>
        <p className="addon-group__description">
          Elige un vehículo existente o crea uno nuevo para cada producto
        </p>
      </header>
      <div className="space-y-6">
        <Accordion title="Información de propiedades" isOpenAccordion={true}>
          {propertyEntries2.map((entry) => {
            const entryKey = `${entry.id}-${entry.count}`;
            const selectedProperty = selectedPropertyByKey[entryKey];
            const showForm =
              !isVehicleType(entry.type) || selectedProperty === null;
            const listProps = propertyListPropsByKey[entryKey];

            return (
              <div key={entryKey} className="custom-box">
                <div className="custom-container">
                  <img src={getIcon(entry.type)} alt="Tipo de propiedad" />
                  <h4 className="custom-title">
                    {entry.alias_name} {entry.count}
                  </h4>
                </div>
                {isVehicleType(entry.type) && listProps && onSelectPropertyForSlot && (
                  <div className="custom-box__select">
                    <PropertyList
                      list={listProps.list}
                      selectedProperty={selectedProperty}
                      onSelectProperty={(p) =>
                        onSelectPropertyForSlot(entryKey, p)
                      }
                      productLabel={`${entry.alias_name} ${entry.count}`}
                      loading={loadingProperties}
                      error={errorProperties}
                      yearRange={listProps.yearRange}
                      stripePriceId={listProps.stripePriceId}
                      hideHeader
                    />
                  </div>
                )}
                {showForm && (
              <div className="custom-grid">
                {entry.properties
                  .filter((prop) => {
                    // Solo mostrar 'date_marbete' para vehículos en Puerto Rico (PR)
                    if (prop.name_intl_eng === 'date_marbete') {
                      const isVehicle =
                        entry.type.toLowerCase() === 'vehiculos';
                      const isPR = branch.toLowerCase() === 'pr';
                      return isVehicle && isPR;
                    }
                    return true;
                  })
                  .sort((a, b) => a.order_number - b.order_number)
                  .map((prop) => (
                    <div key={prop.id} className="custom-field">
                      <CustomPropertyLabel
                        htmlFor={`${entry.id}-${entry.count}-${prop.id}`}
                        required={prop.required}
                      >
                        {prop.name}
                      </CustomPropertyLabel>
                      {prop.type === 'select' ? (
                        prop.name === 'Marca' ? (
                          <CustomPropertySelect
                            id={`${entry.id}-${entry.count}-${prop.id}`}
                            value={
                              formData[`${entry.id}-${entry.count}`]?.[
                                prop.name
                              ] || ''
                            }
                            onChange={(value) => {
                              handleInputChange(
                                `${entry.id}-${entry.count}`,
                                prop.name,
                                value
                              );
                              setSelectedBrands((prev) => ({
                                ...prev,
                                [`${entry.id}-${entry.count}`]: value,
                              }));
                            }}
                            options={Object.keys(
                              vehicleData[
                                entry.type.toLocaleLowerCase() === 'vehiculos'
                                  ? 'autos'
                                  : 'motos'
                              ]
                            )}
                            placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                          />
                        ) : prop.name === 'Modelo' ? (
                          <CustomPropertySelect
                            id={`${entry.id}-${entry.count}-${prop.id}`}
                            value={
                              formData[`${entry.id}-${entry.count}`]?.[
                                prop.name
                              ] || ''
                            }
                            onChange={(value) =>
                              handleInputChange(
                                `${entry.id}-${entry.count}`,
                                prop.name,
                                value
                              )
                            }
                            options={
                              selectedBrands[`${entry.id}-${entry.count}`]
                                ? vehicleData[
                                    entry.type.toLocaleLowerCase() ===
                                    'vehiculos'
                                      ? 'autos'
                                      : 'motos'
                                  ][
                                    selectedBrands[`${entry.id}-${entry.count}`]
                                  ] || []
                                : []
                            }
                            placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                          />
                        ) : prop.name === 'Año' && entry.max_property_age ? (
                          <CustomPropertySelect
                            id={`${entry.id}-${entry.count}-${prop.id}`}
                            value={
                              formData[`${entry.id}-${entry.count}`]?.[
                                prop.name
                              ] || ''
                            }
                            onChange={(value) =>
                              handleInputChange(
                                `${entry.id}-${entry.count}`,
                                prop.name,
                                value
                              )
                            }
                            options={(() => {
                              const currentYear = new Date().getFullYear();
                              const minYear =
                                entry.min_property_age === null
                                  ? currentYear
                                  : currentYear - entry.min_property_age;
                              const maxYear =
                                currentYear - entry.max_property_age;
                              return Array.from(
                                { length: minYear - maxYear + 1 },
                                (_, i) => (minYear - i).toString()
                              );
                            })()}
                            placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                          />
                        ) : prop.name === 'Pais' ? (
                          <CustomPropertySelect
                            id={`${entry.id}-${entry.count}-${prop.id}`}
                            value={
                              formData[`${entry.id}-${entry.count}`]?.[
                                prop.name
                              ] || ''
                            }
                            onChange={(value) =>
                              handleInputChange(
                                `${entry.id}-${entry.count}`,
                                prop.name,
                                value
                              )
                            }
                            options={countryOptions}
                            placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                          />
                        ) : prop.name?.toLowerCase() === 'color' ? (
                          <CustomPropertyColorSelect
                            id={`${entry.id}-${entry.count}-${prop.id}`}
                            value={
                              formData[`${entry.id}-${entry.count}`]?.[
                                prop.name
                              ] || ''
                            }
                            onChange={(value) =>
                              handleInputChange(
                                `${entry.id}-${entry.count}`,
                                prop.name,
                                value
                              )
                            }
                            colors={vehiclesColors}
                            placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                          />
                        ) : null
                        ) : prop.type === 'date' ? (
                        <CustomPropertyDate
                          id={`${entry.id}-${entry.count}-${prop.id}`}
                          value={
                            formData[`${entry.id}-${entry.count}`]?.[
                              prop.name
                            ] || ''
                          }
                          onChange={(value) =>
                            handleInputChange(
                              `${entry.id}-${entry.count}`,
                              prop.name,
                              value
                            )
                          }
                          placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                          required={prop.required}
                          minDate={minDate}
                          maxDate={maxDate}
                        />
                      ) : prop.name?.toLowerCase() === 'color' ? (
                        <CustomPropertyColorSelect
                          id={`${entry.id}-${entry.count}-${prop.id}`}
                          value={
                            formData[`${entry.id}-${entry.count}`]?.[
                              prop.name
                            ] || ''
                          }
                          onChange={(value) =>
                            handleInputChange(
                              `${entry.id}-${entry.count}`,
                              prop.name,
                              value
                            )
                          }
                          colors={vehiclesColors}
                          placeholder={`Seleccionar ${prop.name.toLowerCase()}`}
                        />
                      ) : (
                        <>
                          <CustomPropertyInput
                            id={`${entry.id}-${entry.count}-${prop.id}`}
                            type={prop.type === 'number' ? 'number' : 'text'}
                            placeholder={prop.name}
                            value={
                              formData[`${entry.id}-${entry.count}`]?.[
                                prop.name
                              ] || ''
                            }
                            onChange={(value) => {
                              const isPlate =
                                prop.name_intl_eng?.toLowerCase() === 'plate';
                              const isVin =
                                prop.name?.toLowerCase() === 'vin' ||
                                prop.name_intl_eng?.toLowerCase() === 'vin';
                              const sanitized = isVin
                                ? sanitizeVinInput(value)
                                : isPlate
                                  ? value.replace(/[^a-zA-Z0-9]/g, '')
                                  : value;
                              handleInputChange(
                                `${entry.id}-${entry.count}`,
                                prop.name,
                                sanitized
                              );
                            }}
                            required={prop.required}
                          />
                          {(prop.name?.toLowerCase() === 'vin' ||
                            prop.name_intl_eng?.toLowerCase() === 'vin') &&
                            (() => {
                              const vinValue =
                                formData[`${entry.id}-${entry.count}`]?.[
                                  prop.name
                                ] ?? '';
                              return vinValue.length > 0 && !isValidVin(vinValue) ? (
                                <p
                                  className="custom-field__error"
                                  role="alert"
                                >
                                  {VIN_ERROR_MESSAGE}
                                </p>
                              ) : null;
                            })()}
                        </>
                      )}
                    </div>
                  ))}
              </div>
                )}
              </div>
            );
          })}
        </Accordion>
      </div>
    </CardBody>
  );
}
